import * as React from 'react';
import { api } from './api';

const YEAR_STORAGE_KEY = 'fbp.selectedYear.v1';

function loadSelectedYear() {
  try {
    const raw = window.localStorage.getItem(YEAR_STORAGE_KEY);
    if (raw) {
      const year = Number(raw);
      if (year >= 2000 && year <= 3000) {
        return year;
      }
    }
  } catch (e) {
    // ignore
  }
  return new Date().getFullYear();
}

function saveSelectedYear(year) {
  try {
    window.localStorage.setItem(YEAR_STORAGE_KEY, String(year));
  } catch (e) {
    // ignore
  }
}

const PlannerActionContext = React.createContext({
  saveBudget: null,
  syncBudget: null,
  getFireflyData: null,
  saving: false,
  syncing: false,
  refreshingFirefly: false,
  selectedBudgetId: null,
  selectedYear: new Date().getFullYear(),
  budgets: [],
  setNavActions: () => {},
});

export function PlannerActionProvider({ children }) {
  const [selectedYear, setSelectedYearState] = React.useState(loadSelectedYear);
  const [budgets, setBudgets] = React.useState([]);
  const [selectedBudgetId, setSelectedBudgetId] = React.useState(null);
  const [loadingYear, setLoadingYear] = React.useState(false);

  const [navState, setNavState] = React.useState({
    saveBudget: null,
    syncBudget: null,
    getFireflyData: null,
    saving: false,
    syncing: false,
    refreshingFirefly: false,
    selectedBudgetId: null,
  });

  // Load budgets on mount
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const budgetList = await api.listPlannerBudgets();
        if (!cancelled) {
          setBudgets(budgetList);
        }
      } catch (e) {
        // ignore — caller handles errors
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // When year or budgets change, find the matching budget
  React.useEffect(() => {
    const budget = budgets.find((b) => b.year === selectedYear);
    setSelectedBudgetId(budget ? budget.id : null);
  }, [selectedYear, budgets]);

  // Persist selected year
  const setSelectedYear = React.useCallback((year) => {
    setSelectedYearState(year);
    saveSelectedYear(year);
  }, []);

  const refreshBudgets = React.useCallback(async () => {
    const budgetList = await api.listPlannerBudgets();
    setBudgets(budgetList);
  }, []);

  // Global getFireflyData for the selected year
  const getFireflyData = React.useCallback(async () => {
    const budget = budgets.find((b) => b.year === selectedYear);
    if (!budget) {
      return;
    }
    setLoadingYear(true);
    try {
      await api.refreshPlannerBudget(budget.id);
      await refreshBudgets();
    } finally {
      setLoadingYear(false);
    }
  }, [selectedYear, budgets, refreshBudgets]);

  const setNavActions = React.useCallback((actions) => {
    setNavState((prev) => ({ ...prev, ...actions }));
  }, []);

  const value = React.useMemo(
    () => ({
      ...navState,
      setNavActions,
      selectedYear,
      setSelectedYear,
      budgets,
      selectedBudgetId,
      getFireflyData,
      loadingYear,
      refreshBudgets,
    }),
    [navState, setNavActions, selectedYear, setSelectedYear, budgets, selectedBudgetId, getFireflyData, loadingYear, refreshBudgets],
  );

  return (
    <PlannerActionContext.Provider value={value}>
      {children}
    </PlannerActionContext.Provider>
  );
}

export function usePlannerActions() {
  return React.useContext(PlannerActionContext);
}

export default PlannerActionContext;
